import { ChevronRight, Eye, Printer, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { apiErrorMessage } from '@/i18n'
import { Panel } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { HrSurface } from '@/features/hr/personel/components/HrSurface'
import { BelgeInfoStep } from './components/BelgeInfoStep'
import { PublishSidebar } from './components/PublishSidebar'
import { TemplatePreview } from './components/TemplatePreview'
import { TemplateSourceStep } from './components/TemplateSourceStep'
import { VariableMappingPanel } from './components/VariableMappingPanel'
import { WizardNav } from './components/WizardNav'
import { WizardStepper } from './components/WizardStepper'
import { createTemplateFromWizard, publishTemplate, useDocumentTypes } from './api'
import { emptySistemSurum, validateForPublish } from './lib/templateHelpers'
import { clearWizardDraft, loadWizardDraft, saveWizardDraft } from './lib/wizardDraft'
import { emptyBelgeInfo, belgeInfoSchema, type BelgeInfoValues } from './schemas/belgeInfoSchema'
import type { PublishMode, SablonSurum, WizardStep } from './types'

function readWizardBoot() {
  const draft = loadWizardDraft()
  if (draft?.info && draft.surum) return draft
  return null
}

export function SablonOlusturPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const docTypes = useDocumentTypes().data ?? []
  const [boot] = useState(readWizardBoot)
  const [initialSurum] = useState(() => boot?.surum ?? emptySistemSurum('draft', 1))
  const [step, setStep] = useState<WizardStep>(() => (boot?.step as WizardStep) || 1)
  const [info, setInfo] = useState<BelgeInfoValues>(() => boot?.info ?? emptyBelgeInfo())
  const [infoErrors, setInfoErrors] = useState<Record<string, string>>({})
  const [surum, setSurum] = useState<SablonSurum>(initialSurum)
  const [dirty, setDirty] = useState(() => !!boot?.dirty)
  const [publishMode, setPublishMode] = useState<PublishMode>('future_only')
  const [employeeIds, setEmployeeIds] = useState<string[]>([])
  const [changeReason, setChangeReason] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    if (boot) toast.info(t('hr.belgeler.toasts.draftRestored'))
  }, [boot, t])

  useEffect(() => {
    if (!dirty) return
    const id = window.setTimeout(() => {
      saveWizardDraft({ step, info, surum, dirty: true })
    }, 600)
    return () => window.clearTimeout(id)
  }, [step, info, surum, dirty])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  function patchInfo(patch: Partial<BelgeInfoValues>) {
    setInfo((s) => ({ ...s, ...patch }))
    setDirty(true)
  }

  function patchSurum(next: SablonSurum) {
    setSurum(next)
    setDirty(true)
  }

  function validateStep1(): boolean {
    const parsed = belgeInfoSchema.safeParse(info)
    if (parsed.success) {
      setInfoErrors({})
      return true
    }
    const errs: Record<string, string> = {}
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0] ?? 'ad')
      errs[key] = t('hr.belgeler.form.fieldRequired')
    }
    setInfoErrors(errs)
    toast.error(t('hr.belgeler.toasts.infoInvalid'))
    return false
  }

  function goNext() {
    if (step === 1 && !validateStep1()) return
    if (step === 2 && surum.method !== 'sistem' && !surum.uploadedFile) {
      toast.error(t('hr.belgeler.toasts.fileRequired'))
      return
    }
    setStep((s) => Math.min(4, s + 1) as WizardStep)
  }

  function goBack() {
    setStep((s) => Math.max(1, s - 1) as WizardStep)
  }

  async function saveDraft() {
    if (!validateStep1()) return
    try {
      const { sablon } = await createTemplateFromWizard(info, { ...surum, status: 'taslak' }, docTypes)
      setDirty(false)
      toast.success(t('hr.belgeler.toasts.draftSaved'))
      navigate(`/hr/belgeler/sablonlar/${sablon.id}/duzenle`)
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  async function doPublish() {
    if (!validateStep1()) return
    const errors = validateForPublish(surum)
    if (errors.length) {
      toast.error(t('hr.belgeler.toasts.publishBlocked'))
      return
    }
    if (publishMode === 'selected_employees' && employeeIds.length === 0) {
      toast.error(t('hr.belgeler.toasts.selectEmployees'))
      return
    }
    try {
      const note = changeReason.trim() || t('hr.belgeler.publish.firstPublish')
      const { surum: saved } = await createTemplateFromWizard(info, { ...surum, changeReason: note, status: 'taslak' }, docTypes)
      const result = await publishTemplate(saved, { changeReason: note, mode: publishMode, employeeIds })
      if (!result.ok) {
        toast.error(t('hr.belgeler.toasts.publishBlocked'))
        return
      }
      setDirty(false)
      clearWizardDraft()
      toast.success(t('hr.belgeler.toasts.published'))
      navigate('/hr/belgeler')
    } catch (error) {
      toast.error(apiErrorMessage(error))
    }
  }

  async function tryLeave() {
    if (dirty) {
      const ok = await confirmModal({
        title: t('hr.belgeler.unsavedTitle'),
        description: t('hr.belgeler.unsavedConfirm'),
        destructive: true,
        confirmLabel: t('common.confirm'),
      })
      if (!ok) return
    }
    clearWizardDraft()
    navigate('/hr/belgeler')
  }

  return (
    <HrSurface density="editor">
      <PageHeader
        backTo="/hr/belgeler"
        onBack={() => void tryLeave()}
        eyebrow={
          <nav aria-label={t('hr.belgeler.listTitle')} className="flex items-center gap-1">
            <Link to="/hr/belgeler" className="hover:text-foreground">
              {t('hr.belgeler.listTitle')}
            </Link>
            <ChevronRight className="size-3" />
            <span className="text-foreground">{t('hr.belgeler.newCrumb')}</span>
          </nav>
        }
        title={t('hr.belgeler.createTitle')}
        description={t('hr.belgeler.createSubtitle')}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
              <Eye />
              {t('hr.belgeler.previewAction')}
            </Button>
            <Button type="button" onClick={saveDraft}>
              <Save />
              {t('hr.belgeler.saveDraft')}
            </Button>
          </>
        }
      />

      <WizardStepper current={step} onStepClick={setStep} />

      {step === 1 ? <BelgeInfoStep info={info} errors={infoErrors} onChange={patchInfo} /> : null}

      {step === 2 ? <TemplateSourceStep surum={surum} onChange={patchSurum} /> : null}

      {step === 3 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <Panel icon={Eye} title={t('hr.belgeler.wizard.steps.preview')} hint={t('hr.belgeler.wizard.hints.preview')} bodyClassName="p-0 sm:p-0">
            <div className="min-h-[60dvh] overflow-hidden">
              <TemplatePreview surum={surum} />
            </div>
          </Panel>
          <Panel icon={Printer} tone="orange" title={t('hr.belgeler.preview.exportTitle')} className="h-fit">
            <p className="text-[13px] leading-relaxed text-muted-foreground">{t('hr.belgeler.preview.exportBody')}</p>
            <Button type="button" variant="outline" size="sm" className="mt-3 w-full" onClick={() => window.print()}>
              <Printer />
              {t('hr.belgeler.preview.print')}
            </Button>
          </Panel>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-4">
            {surum.method === 'docx' ? (
              <VariableMappingPanel surum={surum} onChange={(variableMappings) => patchSurum({ ...surum, variableMappings })} />
            ) : null}
            <Panel icon={Eye} title={t('hr.belgeler.wizard.steps.preview')} hint={t('hr.belgeler.wizard.hints.publish')} bodyClassName="p-0 sm:p-0">
              <div className="min-h-[24rem] overflow-hidden">
                <TemplatePreview surum={surum} defaultZoom={0.8} />
              </div>
            </Panel>
          </div>
          <PublishSidebar
            surum={surum}
            versionLabel="v1"
            changeReason={changeReason}
            onChangeReason={setChangeReason}
            mode={publishMode}
            onModeChange={setPublishMode}
            employeeIds={employeeIds}
            onEmployeeIdsChange={setEmployeeIds}
            showVersionInput
          />
        </div>
      ) : null}

      <WizardNav step={step} onBack={goBack} onNext={goNext} onPublish={doPublish} />

      <Modal open={previewOpen} onOpenChange={setPreviewOpen} icon={<Eye />} title={t('hr.belgeler.previewAction')} size="xl" bodyClassName="overflow-auto scrollbar-none p-0">
        <TemplatePreview surum={surum} />
      </Modal>
    </HrSurface>
  )
}
